/**
 * Spec №11 Stage 3 — helpers for the declared-view renderers. Pure functions
 * that turn a ViewNodeConfig + an entity type into columns, labels, and display
 * strings, reusing the existing v3 column primitives. Design-rules: sentence
 * case labels, references resolved to names, tabular figures via formatCellValue.
 */
import type {
  EntityTypeDefinition,
  FieldDefinition,
  ResolvedRef,
  ViewNodeConfig,
  ViewFieldOverride,
} from "../../lib/types";
import { getColumnsFor, buildVirtualColumns, formatCellValue, type TableColumn } from "./v3-columns";

/** Sentence-case a field name (design-rules §3), unless an explicit label wins. */
export function sentenceLabel(name: string, override?: string): string {
  if (override && override.trim()) return override.trim();
  const s = name.replace(/[_-]+/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : name;
}

export function overrideFor(
  cfg: ViewNodeConfig | undefined,
  field: string,
): ViewFieldOverride | undefined {
  return cfg?.field_overrides?.find((o) => o.field === field);
}

/** Columns for a declared list view: the type's columns, filtered + relabelled
 *  by field_overrides (visible:false drops the column; label overrides; format
 *  honoured). Falls back to the type's default columns when no config. */
export function listColumns(
  entityType: EntityTypeDefinition,
  cfg?: ViewNodeConfig,
): TableColumn[] {
  const body = getColumnsFor(entityType)
    .filter((c) => overrideFor(cfg, c.field)?.visible !== false)
    .map((c) => {
      const o = overrideFor(cfg, c.field);
      return { ...c, label: sentenceLabel(c.field, o?.label), format: o?.format ?? c.format };
    });
  // The record's ID (e.g. a matter number) is the first column of EVERY declared
  // list — a systemic default, not per-list wiring: you always see which row you
  // are looking at. Monospace, labelled "ID". A firm can still hide or relabel it
  // per view via a field override on `id`, exactly like any other column.
  const idOverride = overrideFor(cfg, "id");
  if (idOverride?.visible === false) return body;
  const idCol: TableColumn = { ...buildVirtualColumns().before[0]!, label: idOverride?.label ?? "ID" };
  return [idCol, ...body];
}

/** A lookup of the type's columns by field name (for record value formatting). */
export function columnsByName(entityType: EntityTypeDefinition): Record<string, TableColumn> {
  return Object.fromEntries(getColumnsFor(entityType).map((c) => [c.field, c]));
}

/** Display string for a field value, resolving references via forward_refs. */
export function fieldDisplay(
  field: FieldDefinition,
  value: unknown,
  forwardRefs: Record<string, ResolvedRef[]> | undefined,
  cols: Record<string, TableColumn>,
): string {
  if (field.type === "reference" || field.type === "references") {
    const refs = forwardRefs?.[field.name];
    if (refs && refs.length) return refs.map((r) => (r.restricted ? "Restricted" : r.name)).join(", ");
    return value == null ? "" : String(value);
  }
  const col = cols[field.name];
  return col ? formatCellValue(value, col) : value == null ? "" : String(value);
}

/** Apply the view's first sort spec to rows, client-side. */
export function sortRows<T extends Record<string, unknown>>(rows: T[], cfg?: ViewNodeConfig): T[] {
  const sort = cfg?.query?.sorts?.[0];
  if (!sort) return rows;
  return applySort(rows, { field: sort.field, dir: sort.direction === "desc" ? "desc" : "asc" });
}

// ---------------------------------------------------------------------------
// №14.1.1-D (D-DS5) — the declared-view toolbar model. Search, filters and sort
// are engine-side helpers so every declared list (`PlaceListPage`) inherits them
// with zero per-view code. Search + option/status/reference filters push to the
// server (the №11.1 bounded-read `list_*` accepts `search` + field-equality —
// see `src/engine/entities.ts`), so the toolbar never fetches-everything. Sort
// re-orders the returned (already server-bounded) page client-side, matching the
// pre-existing `sortRows` behaviour; the caps are the server's, not ours.
// ---------------------------------------------------------------------------

export type SortDir = "asc" | "desc";
export interface SortSpec {
  field: string;
  dir: SortDir;
}

/** Parse the `sort` URL param (`field:dir`) into a spec, or undefined. */
export function parseSortParam(raw: string | null | undefined): SortSpec | undefined {
  if (!raw) return undefined;
  const [field, dir] = raw.split(":");
  if (!field) return undefined;
  return { field, dir: dir === "desc" ? "desc" : "asc" };
}

export function serializeSort(spec: SortSpec): string {
  return `${spec.field}:${spec.dir}`;
}

/** The view's declared default sort (first sort spec), as a SortSpec. */
export function defaultSortSpec(cfg?: ViewNodeConfig): SortSpec | undefined {
  const s = cfg?.query?.sorts?.[0];
  return s ? { field: s.field, dir: s.direction === "desc" ? "desc" : "asc" } : undefined;
}

/** Stable, null-last, type-aware client-side sort of an already-bounded page. */
export function applySort<T extends Record<string, unknown>>(rows: T[], spec?: SortSpec): T[] {
  if (!spec) return rows;
  const dir = spec.dir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = a[spec.field] as string | number | undefined;
    const bv = b[spec.field] as string | number | undefined;
    if (av == null && bv == null) return 0;
    if (av == null) return 1; // nulls sort last regardless of direction
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") {
      return av < bv ? -dir : av > bv ? dir : 0;
    }
    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
    return cmp * dir;
  });
}

/** A readable name for an entity row (its title column, else id). Used to label
 *  reference-filter options + the active reference-filter value. */
export function entityDisplayName(
  row: Record<string, unknown>,
  type: EntityTypeDefinition,
): string {
  const title = getColumnsFor(type).find((c) => c.isTitle);
  const v = title ? row[title.field] : undefined;
  if (v != null && v !== "") return String(v);
  return String(row.id ?? "");
}

/** How a filter's option list is sourced + posted to the server. */
export type FilterKind = "enum" | "status" | "reference";

export interface FilterSpec {
  /** The `list_*` filter key (a declared field name) + the URL param name. */
  field: string;
  /** Chip label (sentence-cased field name, or the view override). */
  label: string;
  kind: FilterKind;
  /** For enum/status: the fixed option values. For reference: the target slug. */
  choices?: string[];
  target?: string;
}

/**
 * The set of filters a declared list surfaces, per the spec default: every
 * option-set/enum field and the workflow status field. Reference fields become
 * filters too (the engine filters them by target id server-side); their option
 * list is loaded lazily by the toolbar when the chip is opened. Date-range
 * filtering is intentionally NOT surfaced here — the `list_*` engine has no
 * range predicate, and a client-side range over a server-capped page would be
 * misleading (documented Stage-D deviation).
 */
export function filterSpecs(
  entityType: EntityTypeDefinition,
  cfg?: ViewNodeConfig,
): FilterSpec[] {
  const statusField = entityType.workflow?.field ?? (entityType.workflow ? "status" : undefined);
  const specs: FilterSpec[] = [];
  for (const f of entityType.fields) {
    if ((f.display_role as string) === "hidden") continue;
    // A visible:false override drops the column AND its filter.
    if (overrideFor(cfg, f.name)?.visible === false) continue;
    const label = sentenceLabel(f.name, overrideFor(cfg, f.name)?.label);
    if (statusField && f.name === statusField) {
      const states = entityType.workflow?.states?.map((s) => s.slug) ?? f.choices ?? [];
      if (states.length > 0) specs.push({ field: f.name, label, kind: "status", choices: states });
    } else if (f.type === "enum") {
      if ((f.choices?.length ?? 0) > 0) specs.push({ field: f.name, label, kind: "enum", choices: f.choices });
    } else if (f.type === "reference" && f.target) {
      specs.push({ field: f.name, label, kind: "reference", target: f.target });
    }
  }
  return specs;
}
